import {useState} from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldGroup,
  FieldLabel,
  Input,
  Textarea
} from '@kroll-resolver-crisp/ui-components-react';
import {FlagIcon, useFlagLabel} from './Flag';
import {getApiErrorMessage} from '@/api/client';
import {useFlags, useRenameFlags} from '@/api/flagHooks';
import {
  FLAG_COLORS,
  FLAG_LABEL_MAX_LENGTH,
  FLAG_NOTE_MAX_LENGTH,
  type FlagColor
} from '@/api/flags';
import {showToast} from '@/lib/toast';

// Why the analyst flagged this item - personal, like the flag. Saving blank clears it. With
// `chooseColor` it is the "flag with a note" dialog for an unflagged item: pick the colour and the
// reason together, and both are saved as one flag.
export function FlagNoteDialog({
  open,
  onOpenChange,
  subject,
  note,
  chooseColor = false,
  onSave
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly subject: string;
  readonly note: string | null | undefined;
  readonly chooseColor?: boolean;
  readonly onSave: (note: string | null, color: FlagColor) => void;
}) {
  const labelOf = useFlagLabel();
  const [draft, setDraft] = useState(note ?? '');
  const [color, setColor] = useState<FlagColor>(FLAG_COLORS[0]);
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) {
          setDraft(note ?? '');
          setColor(FLAG_COLORS[0]);
        }

        onOpenChange(next);
      }}>
      <DialogContent className="sm:max-w-md [&>*]:min-w-0">
        <DialogHeader>
          <DialogTitle>{chooseColor ? 'Flag with a note' : 'Flag note'}</DialogTitle>
          <DialogDescription className="truncate">{subject}</DialogDescription>
        </DialogHeader>
        {chooseColor ? (
          <div role="radiogroup" aria-label="Flag colour" className="flex flex-wrap gap-1">
            {FLAG_COLORS.map((c) => (
              <Button
                key={c}
                type="button"
                role="radio"
                aria-checked={color === c}
                variant={color === c ? 'secondary' : 'outline'}
                size="xs"
                onClick={() => {
                  setColor(c);
                }}>
                <FlagIcon color={c} />
                {labelOf(c)}
              </Button>
            ))}
          </div>
        ) : null}
        <Field>
          <FieldLabel htmlFor="flag-note">Why did you flag this?</FieldLabel>
          <Textarea
            id="flag-note"
            value={draft}
            maxLength={FLAG_NOTE_MAX_LENGTH}
            rows={5}
            placeholder="Only you can see this note."
            onChange={(event) => {
              setDraft(event.target.value);
            }}
          />
        </Field>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              onSave(draft.trim() || null, color);
              onOpenChange(false);
            }}>
            {chooseColor ? 'Flag' : 'Save note'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// An analyst's own names for the eight colours ("Chase up", "Escalate"). Blank = the colour's own name.
export function FlagLabelsDialog({
  open,
  onOpenChange
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <FlagNamesForm
          onDone={() => {
            onOpenChange(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

// Mounted only while the dialog is open (Radix unmounts closed content), so `draft` starts empty on
// every open. It holds only the names the analyst has edited here; every other colour shows, and
// keeps, what the server already has. Seeding the draft from the saved names on open was the bug:
// the parent opens the dialog by setting `open`, which never fires onOpenChange(true), so the draft
// stayed empty and saving sent only what had just been typed.
function FlagNamesForm({onDone}: {readonly onDone: () => void}) {
  const {data: flags = []} = useFlags();
  const {mutate, isPending} = useRenameFlags();
  const labelOf = useFlagLabel();
  const [draft, setDraft] = useState<Partial<Record<FlagColor, string>>>({});
  const savedLabel = (color: FlagColor) => flags.find((f) => f.color === color)?.label ?? '';
  return (
    <>
      <DialogHeader>
        <DialogTitle>Name your flags</DialogTitle>
        <DialogDescription>
          Give a colour a meaning. Only you see these names; leave one blank to keep the colour.
        </DialogDescription>
      </DialogHeader>
      <FieldGroup className="gap-2">
        {FLAG_COLORS.map((color) => (
          <Field key={color} orientation="horizontal">
            <FieldLabel htmlFor={`flag-label-${color}`} className="w-8 shrink-0">
              <FlagIcon color={color} />
            </FieldLabel>
            <Input
              id={`flag-label-${color}`}
              aria-label={`Name for ${color} flag`}
              value={draft[color] ?? savedLabel(color)}
              maxLength={FLAG_LABEL_MAX_LENGTH}
              placeholder={labelOf(color, false)}
              onChange={(event) => {
                setDraft((previous) => ({...previous, [color]: event.target.value}));
              }}
            />
          </Field>
        ))}
      </FieldGroup>
      <DialogFooter>
        <Button variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button
          disabled={isPending}
          onClick={() => {
            // Only the colours whose name actually changed; a blank one un-names it.
            const renames = FLAG_COLORS.flatMap((color) => {
              const label = draft[color]?.trim();
              return label === undefined || label === savedLabel(color)
                ? []
                : [{color, label: label || null}];
            });
            if (renames.length === 0) {
              onDone();
              return;
            }

            mutate(renames, {
              onSuccess: onDone,
              onError(error) {
                showToast(getApiErrorMessage(error, 'Failed to save flag names.'), 'error');
              }
            });
          }}>
          Save names
        </Button>
      </DialogFooter>
    </>
  );
}
